/**
 * EL MOTOR DE COLOR DEL TEMA DEL PORTAL — de tres colores, los tokens del portal en claro y oscuro.
 *
 * Juan, 2026-09-25: el tema del portal se escoge por comercio en la pestaña «Temas» de la ficha,
 * y *«si toma un color oscuro las letras van a ser claras»*. Este archivo es esa regla: OKLCH para
 * decidir los colores y el contraste de WCAG para comprobar que se leen.
 *
 * Aprobado mirando el prototipo publicado (claude.ai/artifact/BzWKzVYwSbPgu23teDyZnu): el motor
 * de aquí es el MISMO que el de esa página, pasado a TypeScript. Si se cambia uno, se cambia el otro.
 *
 * 🔴 Un tema son los MISMOS tokens del portal (`app/globals.css` de portal-comercios), reescritos.
 * No cambia letras, tamaños ni disposición, y NUNCA toca los colores de estado (verde, ámbar,
 * rojo): el color del dato no es del tema.
 *
 * 🔴 Lo que se guarda en el núcleo son los tokens YA CALCULADOS, no la receta: así el portal pinta
 * exactamente lo que se vio en la vista previa. La validación de la forma vive en
 * `kredit-api-MT/helpers/tema-comercio.js`, con la MISMA lista de `TOKENS`.
 */

export type Tokens = Record<string, string>;
export type Preset = 'principal' | 'creditotal' | 'creditotal-2' | 'personalizado';
/** `barra`: el color de la barra lateral en modo claro (#RRGGBB). La primera versión guardaba
 *  «base» o «primary»; `generarTema` y `temaGuardado` todavía los entienden. */
/** `fondo`: el fondo del modo claro (#RRGGBB). Los temas guardados antes no lo traen: ahí sale del tinte de la base. */
/** `panel`: el panel de afuera del ingreso (el negro de siempre en el Principal). */
export interface Colores { principal: string; acento: string; base: string; barra: string; fondo: string; panel: string; }
export interface Oklch { l: number; c: number; h: number; }
export interface Juegos { claro: Tokens; oscuro: Tokens; notas: string[]; }

/** Los tokens que un tema pisa. La MISMA lista está en el núcleo y en el portal. */
export const TOKENS = [
  'background', 'foreground', 'card', 'card-foreground', 'popover', 'popover-foreground',
  'primary', 'primary-foreground', 'secondary', 'secondary-foreground',
  'muted', 'muted-foreground', 'accent', 'accent-foreground',
  'border', 'input', 'ring',
  'sidebar', 'sidebar-foreground', 'sidebar-muted', 'sidebar-primary', 'sidebar-primary-foreground',
  'sidebar-accent', 'sidebar-accent-foreground', 'sidebar-border', 'sidebar-ring',
  'sidebar-activo', 'sidebar-activo-foreground', 'sidebar-activo-borde',
  'marca', 'marca-foreground',
  // el panel del ingreso: en el núcleo y en el portal son OPCIONALES (los temas viejos no los traen)
  'panel-ingreso', 'panel-ingreso-foreground', 'panel-ingreso-muted',
];

// ─────────────────────────────────────────────────────────────── la matemática del color
const clamp = (x: number, a: number, b: number): number => Math.min(b, Math.max(a, x));
export const O = (l: number, c: number, h: number): Oklch => ({ l, c, h: ((h % 360) + 360) % 360 });

function hexARgb(h: string): number[] {
  h = h.replace('#', '');
  if (h.length === 3) h = h.split('').map((x) => x + x).join('');
  const n = parseInt(h.slice(0, 6), 16);
  return [(n >> 16 & 255) / 255, (n >> 8 & 255) / 255, (n & 255) / 255];
}
const rgbAHex = (rgb: number[]): string =>
  '#' + rgb.map((v) => Math.round(clamp(v, 0, 1) * 255).toString(16).padStart(2, '0')).join('').toUpperCase();
const lin = (v: number): number => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
const srgb = (v: number): number => (v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055);

function rgbAOklab(rgb: number[]): number[] {
  const [r, g, b] = rgb.map(lin);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s,
  ];
}
function oklabALin([L, A, B]: number[]): number[] {
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.2914855480 * B) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s,
  ];
}
export function oDe(hex: string): Oklch {
  const [L, A, B] = rgbAOklab(hexARgb(hex));
  let h = Math.atan2(B, A) * 180 / Math.PI;
  if (h < 0) h += 360;
  return { l: L, c: Math.hypot(A, B), h };
}
const enGama = (v: number[]): boolean => v.every((x) => x >= -1e-4 && x <= 1 + 1e-4);
/** Fuera de la gama del monitor se baja el croma, nunca la luz ni el tono. */
export function hexDe(o: Oklch): string {
  const lab = (c: number): number[] => [o.l, c * Math.cos(o.h * Math.PI / 180), c * Math.sin(o.h * Math.PI / 180)];
  let v = oklabALin(lab(o.c));
  if (!enGama(v)) {
    let lo = 0, hi = o.c;
    for (let i = 0; i < 22; i++) { const c = (lo + hi) / 2; if (enGama(oklabALin(lab(c)))) lo = c; else hi = c; }
    v = oklabALin(lab(lo));
  }
  return rgbAHex(v.map((x) => srgb(clamp(x, 0, 1))));
}
function luz(hex: string): number { const [r, g, b] = hexARgb(hex).map(lin); return 0.2126 * r + 0.7152 * g + 0.0722 * b; }
/** El contraste de WCAG 2, de 1 a 21. 4,5 es el mínimo para leer texto normal. */
export function contraste(a: string, b: string): number {
  const x = luz(a), y = luz(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}
function mezcla(a: string, b: string, t: number): string {
  const A = rgbAOklab(hexARgb(a)), B = rgbAOklab(hexARgb(b));
  return rgbAHex(oklabALin(A.map((v, i) => v + (B[i] - v) * t)).map((x) => srgb(clamp(x, 0, 1))));
}
function distancia(a: string, b: string): number {
  const A = rgbAOklab(hexARgb(a)), B = rgbAOklab(hexARgb(b));
  return Math.hypot(A[0] - B[0], A[1] - B[1], A[2] - B[2]);
}
/** Un color con transparencia (#RRGGBBAA), ya compuesto sobre su fondo, para poder medirlo. */
export function sobre(hexAlfa: string, fondo: string): string {
  if (hexAlfa.length <= 7) return hexAlfa;
  const t = parseInt(hexAlfa.slice(7, 9), 16) / 255, A = hexARgb(hexAlfa), B = hexARgb(fondo);
  return rgbAHex(A.map((v, i) => B[i] + (v - B[i]) * t));
}
/** Mueve la luz de un color hasta que se lea contra TODOS los fondos dados. */
function asegurar(o: Oklch, fondos: string[], meta: number, dir: number): Oklch {
  const x = { ...o };
  for (let i = 0; i < 90; i++) {
    const h = hexDe(x);
    if (fondos.every((f) => contraste(h, f) >= meta)) return x;
    x.l = clamp(x.l + dir * 0.01, 0, 1);
  }
  return x;
}
/** 🔴 La regla de Juan: sobre fondo oscuro, letra clara; sobre fondo claro, letra oscura. Se mide. */
function letraPara(fondo: string, oscura: string, clara: string): string {
  const a = contraste(oscura, fondo), b = contraste(clara, fondo);
  if (Math.max(a, b) >= 4.5) return a >= b ? oscura : clara;
  return contraste('#0A0A0A', fondo) >= contraste('#FFFFFF', fondo) ? '#0A0A0A' : '#FFFFFF';
}

// ─────────────────────────────────────────────────────────────── los tonos de estado, reservados
export interface Reservada { h: number; ancho: number; nombre: string; }
/** Los tonos del semáforo del portal (--ok 155, --aviso 65–85, --malo 25). El acento los esquiva. */
export const RESERVADAS: Reservada[] = [
  { h: 25, ancho: 18, nombre: 'rojo de error' },
  { h: 75, ancho: 20, nombre: 'ámbar de «atención»' },
  { h: 155, ancho: 18, nombre: 'verde de «bien»' },
];
const dTono = (a: number, b: number): number => Math.abs(((a - b + 540) % 360) - 180);
export function zona(o: Oklch): Reservada | null {
  if (o.c < 0.06) return null;
  return RESERVADAS.find((z) => dTono(o.h, z.h) <= z.ancho) || null;
}
function alejar(h: number): number {
  for (const z of RESERVADAS) {
    const d = ((h - z.h + 540) % 360) - 180;
    if (Math.abs(d) <= z.ancho) return (z.h + (d >= 0 ? z.ancho + 5 : -(z.ancho + 5)) + 360) % 360;
  }
  return h;
}

// ─────────────────────────────────────────────────────────────── las propuestas
export interface Propuesta { nombre: string; hex: string; nota?: string; }

export function proponerAcentos(pHex: string): Propuesta[] {
  const p = oDe(pHex), gris = p.c < 0.03, L = clamp(p.l, 0.64, 0.8), C = gris ? 0.13 : clamp(p.c, 0.11, 0.19);
  const reglas: [string, number][] = gris
    ? [['Violeta', 300], ['Coral', 48], ['Turquesa', 190]]
    : [['Complementario', 180], ['Dividido', 150], ['Análogo', 42]];
  const salida: (Propuesta & { h: number })[] = [];
  for (const [nombre, d] of reglas) {
    let h = gris ? d : (p.h + d) % 360;
    if (!gris && zona(O(L, C, h))) { const alt = (p.h - d + 360) % 360; h = zona(O(L, C, alt)) ? alejar(h) : alt; }
    while (salida.some((s) => dTono(s.h, h) < 16)) h = alejar((h + 22) % 360);
    salida.push({ nombre, h, hex: hexDe(O(L, C, h)) });
  }
  return salida.map(({ nombre, hex }) => ({ nombre, hex }));
}

export function proponerBases(pHex: string): Propuesta[] {
  const p = oDe(pHex), gris = p.c < 0.03;
  return [
    { nombre: 'Del mismo tono', nota: 'fondos y bordes con un toque del principal', hex: hexDe(O(0.22, gris ? 0.006 : clamp(p.c * 0.3, 0.02, 0.06), p.h)) },
    { nombre: 'Neutra', nota: 'grises sin tinte', hex: hexDe(O(0.215, 0.004, p.h)) },
  ];
}

/** El fondo de siempre: casi blanco, con un toque del tono de la base. */
export function fondoConTinte(base: string): string {
  const b = oDe(base);
  return hexDe(O(0.985, Math.min(Math.min(b.c, 0.07) * 0.10, 0.006), b.h));
}

/**
 * Los atajos del fondo. Juan, 2026-09-25: *«¿cómo cambio el gris de fondo si no quiero ese gris?»*.
 * Cualquier otro color claro se escoge con el selector.
 */
export function opcionesFondo(C: Colores): Propuesta[] {
  return [{ nombre: 'Blanco', hex: '#FFFFFF' }, { nombre: 'Con tinte', hex: fondoConTinte(C.base) }];
}

/**
 * Los atajos del panel del ingreso. Juan, 2026-09-25: *«el color de afuera del login siempre está
 * en negro; también deberíamos poder seleccionarlo»*.
 */
export function opcionesPanel(C: Colores): Propuesta[] {
  const b = oDe(C.base);
  const tinta = luz(C.base) < 0.03 ? C.base : hexDe(O(0.2, Math.min(b.c, 0.05), b.h));
  return [
    { nombre: 'Negro', hex: '#0A0A0A' },
    { nombre: 'Tinta', hex: tinta },
    { nombre: 'Del principal', hex: C.principal },
    { nombre: 'Claro', hex: fondoConTinte(C.base) },
  ];
}

/** El panel del ingreso y su letra. Va igual en claro y en oscuro, como el negro de siempre. */
function tokensPanel(panel: string, oscura: string, clara: string): Tokens {
  const fg = letraPara(panel, oscura, clara);
  const panelOscuro = contraste(clara, panel) > contraste(oscura, panel);
  return {
    'panel-ingreso': panel,
    'panel-ingreso-foreground': fg,
    'panel-ingreso-muted': hexDe(asegurar(oDe(mezcla(fg, panel, 0.3)), [panel], 4.5, panelOscuro ? 1 : -1)),
  };
}

/**
 * Los atajos de la barra lateral. Juan, 2026-09-25: *«la barra permíteme ponerla o clara o
 * oscura»*. Oscura y clara salen del tono de la base; el tercero es el principal. Cualquier otro
 * color se escoge con el selector.
 */
export function opcionesBarra(C: Colores): Propuesta[] {
  const b = oDe(C.base);
  const oscura = luz(C.base) < 0.03 ? C.base : hexDe(O(0.2, Math.min(b.c, 0.05), b.h));
  const clara = hexDe(O(0.985, Math.min(b.c * 0.15, 0.006), b.h));
  return [{ nombre: 'Oscura', hex: oscura }, { nombre: 'Clara', hex: clara }, { nombre: 'Del principal', hex: C.principal }];
}

// ─────────────────────────────────────────────────────────────── el generador
/** De los tres colores, el de la barra y el del fondo, los dos juegos de tokens del portal. */
export function generarTema(P: string, A: string, B: string, barra: string, fondo?: string, panel?: string): Juegos {
  const p = oDe(P), a = oDe(A), b = oDe(B), hN = b.h, cN = Math.min(b.c, 0.07), notas: string[] = [];
  const tintaOscura = hexDe(O(0.17, Math.min(cN * 0.55, 0.035), hN));
  const tintaClara = hexDe(O(0.985, Math.min(cN * 0.12, 0.008), hN));

  /* ── CLARO ── */
  const c: Tokens = {};
  c['background'] = fondo || fondoConTinte(B);
  /* en modo claro el fondo va claro: un fondo oscuro aquí es el modo oscuro, y ése ya existe */
  if (oDe(c['background']).l < 0.9) {
    const f = oDe(c['background']);
    const claro = hexDe(O(0.93, Math.min(f.c, 0.04), f.h));
    notas.push(`En claro, el fondo se aclaró (${c['background']} → ${claro}): en modo claro el fondo va claro.`);
    c['background'] = claro;
  }
  c['foreground'] = tintaOscura;
  c['card'] = '#FFFFFF'; c['card-foreground'] = c['foreground'];
  c['popover'] = c['card']; c['popover-foreground'] = c['foreground'];
  c['muted'] = hexDe(O(0.962, Math.min(cN * 0.22, 0.014), hN)); c['secondary'] = c['muted']; c['accent'] = c['muted'];
  c['secondary-foreground'] = c['foreground']; c['accent-foreground'] = c['foreground'];
  c['muted-foreground'] = hexDe(asegurar(O(0.52, Math.min(cN * 0.45, 0.03), hN), [c['card'], c['background'], c['muted']], 4.5, -1));
  c['border'] = hexDe(O(0.912, Math.min(cN * 0.28, 0.018), hN)); c['input'] = c['border'];
  let pc = P;
  if (contraste(pc, c['background']) < 1.4) {
    pc = hexDe(asegurar(p, [c['background']], 1.8, -1));
    notas.push(`En claro, el principal se oscureció un poco (${P} → ${pc}): casi no se distinguía del fondo.`);
  }
  c['primary'] = pc; c['primary-foreground'] = letraPara(pc, tintaOscura, '#FFFFFF'); c['ring'] = pc;

  const bar = (barra === 'primary' || barra === P) ? pc : (barra === 'base' || !barra) ? B : barra;
  c['sidebar'] = bar;
  c['sidebar-foreground'] = letraPara(bar, tintaOscura, tintaClara);
  const barraOscura = contraste(tintaClara, bar) > contraste(tintaOscura, bar);
  c['sidebar-muted'] = hexDe(asegurar(oDe(mezcla(c['sidebar-foreground'], bar, 0.32)), [bar], 4.5, barraOscura ? 1 : -1));
  /* el paso del ratón por el menú: apenas un tono sobre la barra */
  c['sidebar-accent'] = mezcla(bar, c['sidebar-foreground'], 0.1);
  c['sidebar-accent-foreground'] = letraPara(c['sidebar-accent'], tintaOscura, tintaClara);
  c['sidebar-primary'] = pc; c['sidebar-primary-foreground'] = c['primary-foreground'];
  c['sidebar-border'] = mezcla(bar, c['sidebar-foreground'], 0.12);
  /* 🔴 EL RENGLÓN ACTIVO. En el portal va con fondo del principal al 15 %, letra del principal y
     un borde de 4 px (sidebar.tsx). Si la barra ES del principal, eso sale cian sobre cian: se
     aclara el fondo, la letra la escoge el contraste y el borde toma el color de la letra. */
  const barraPrincipal = distancia(bar, pc) < 0.08;
  if (barraPrincipal) {
    c['sidebar-activo'] = mezcla(bar, '#FFFFFF', barraOscura ? 0.16 : 0.4);
    c['sidebar-activo-foreground'] = letraPara(c['sidebar-activo'], tintaOscura, tintaClara);
    c['sidebar-activo-borde'] = c['sidebar-activo-foreground'];
  } else {
    c['sidebar-activo'] = mezcla(bar, pc, 0.15);
    c['sidebar-activo-foreground'] = hexDe(asegurar(oDe(pc), [c['sidebar-activo']], 4.5, barraOscura ? 1 : -1));
    c['sidebar-activo-borde'] = pc;
  }
  c['sidebar-ring'] = barraPrincipal ? c['sidebar-foreground'] : pc;
  /* la marca se corrige sólo si se confunde con la barra en luz Y en tono */
  let mc = A;
  if (contraste(mc, bar) < 1.5 && distancia(mc, bar) < 0.15) {
    mc = hexDe(asegurar(a, [bar], 2, barraOscura ? 1 : -1));
    notas.push(`En claro, el acento se ${barraOscura ? 'aclaró' : 'oscureció'} (${A} → ${mc}): sobre la barra no se veía.`);
  }
  c['marca'] = mc; c['marca-foreground'] = letraPara(mc, tintaOscura, '#FFFFFF');
  Object.assign(c, tokensPanel(panel || '#0A0A0A', tintaOscura, tintaClara));

  /* ── OSCURO ── la barra va siempre oscura: una barra clara en modo oscuro deshace el modo */
  const o: Tokens = {};
  o['background'] = hexDe(O(0.16, Math.min(cN * 0.55, 0.035), hN));
  o['foreground'] = hexDe(O(0.975, Math.min(cN * 0.12, 0.008), hN));
  o['card'] = hexDe(O(0.205, Math.min(cN * 0.55, 0.035), hN)); o['card-foreground'] = o['foreground'];
  o['popover'] = o['card']; o['popover-foreground'] = o['foreground'];
  o['muted'] = hexDe(O(0.27, Math.min(cN * 0.5, 0.03), hN)); o['secondary'] = o['muted']; o['accent'] = o['muted'];
  o['secondary-foreground'] = o['foreground']; o['accent-foreground'] = o['foreground'];
  o['muted-foreground'] = hexDe(asegurar(O(0.72, Math.min(cN * 0.25, 0.02), hN), [o['card'], o['background'], o['muted']], 4.5, 1));
  o['border'] = '#FFFFFF1A'; o['input'] = '#FFFFFF26';
  let pd = P;
  if (contraste(pd, o['background']) < 3) {
    pd = hexDe(asegurar(p, [o['background']], 3, 1));
    notas.push(`En oscuro, el principal se aclaró (${P} → ${pd}): sobre el fondo oscuro se perdía.`);
  }
  o['primary'] = pd; o['primary-foreground'] = letraPara(pd, tintaOscura, o['foreground']); o['ring'] = pd;
  o['sidebar'] = hexDe(O(0.195, Math.min(cN * 0.6, 0.04), hN));
  o['sidebar-foreground'] = o['foreground'];
  o['sidebar-muted'] = hexDe(asegurar(O(0.72, Math.min(cN * 0.25, 0.02), hN), [o['sidebar']], 4.5, 1));
  o['sidebar-accent'] = mezcla(o['sidebar'], o['foreground'], 0.08);
  o['sidebar-accent-foreground'] = o['foreground'];
  o['sidebar-primary'] = pd; o['sidebar-primary-foreground'] = o['primary-foreground'];
  o['sidebar-activo'] = mezcla(o['sidebar'], pd, 0.15);
  o['sidebar-activo-foreground'] = hexDe(asegurar(oDe(pd), [o['sidebar-activo']], 4.5, 1));
  o['sidebar-activo-borde'] = pd;
  o['sidebar-border'] = '#FFFFFF1A'; o['sidebar-ring'] = pd;
  let md = A;
  if (contraste(md, o['sidebar']) < 3) {
    md = hexDe(asegurar(a, [o['sidebar']], 3, 1));
    notas.push(`En oscuro, el acento se aclaró (${A} → ${md}): sobre la barra oscura se perdía.`);
  }
  o['marca'] = md; o['marca-foreground'] = letraPara(md, tintaOscura, '#FFFFFF');
  Object.assign(o, tokensPanel(panel || '#0A0A0A', tintaOscura, tintaClara));
  return { claro: c, oscuro: o, notas };
}

// ─────────────────────────────────────────────────────────────── los temas de la lista
/** Juan, 2026-09-29: la barra lateral y el cuadro del ingreso van en el NEGRO del ingreso del Principal
 *  (#0A0A0A = oklch(0.145 0 0), `--panel-ingreso` del portal), no en la tinta: «así como es principal,
 *  pero en el de CrediTotal». La tinta (#040C22) queda para la base y el fondo. */
export const CREDITOTAL: Colores = { principal: '#37B0C9', acento: '#F5A302', base: '#040C22', barra: '#0A0A0A', fondo: fondoConTinte('#040C22'), panel: '#0A0A0A' };
/** Juan, 2026-09-25: *«otro CrediTotal 2… con el azul claro como color sólido del navbar»*. */
export const CREDITOTAL_2: Colores = { ...CREDITOTAL, barra: '#37B0C9' };
export const DE_LA_LISTA: Record<'creditotal' | 'creditotal-2', Colores> = { 'creditotal': CREDITOTAL, 'creditotal-2': CREDITOTAL_2 };
export const NOMBRE_PRESET: Record<Preset, string> = {
  'principal': 'Principal', 'creditotal': 'CrediTotal', 'creditotal-2': 'CrediTotal 2', 'personalizado': 'Personalizado',
};

/**
 * El Principal NO se genera: son los tokens de hoy del portal (neutros de shadcn), en hexadecimal
 * para poder medirlos y dibujar la vista previa. En el núcleo se guarda sin tokens.
 * 🔴 `marca-foreground` en oscuro va #171717: hoy el portal pinta el ícono del cuadro del comercio
 * casi blanco sobre casi blanco (1,2 a 1). Se corrige en el portal a la vez que esto.
 */
export const PRINCIPAL: { claro: Tokens; oscuro: Tokens; colores: Colores } = {
  claro: {
    'background': '#FFFFFF', 'foreground': '#0A0A0A', 'card': '#FFFFFF', 'card-foreground': '#0A0A0A',
    'popover': '#FFFFFF', 'popover-foreground': '#0A0A0A', 'muted': '#F5F5F5', 'secondary': '#F5F5F5', 'accent': '#F5F5F5',
    'secondary-foreground': '#171717', 'accent-foreground': '#171717', 'muted-foreground': '#737373',
    'border': '#E5E5E5', 'input': '#E5E5E5', 'ring': '#A1A1A1', 'primary': '#171717', 'primary-foreground': '#FAFAFA',
    'sidebar': '#FAFAFA', 'sidebar-foreground': '#0A0A0A', 'sidebar-muted': '#525252', 'sidebar-accent': '#F5F5F5',
    'sidebar-accent-foreground': '#171717', 'sidebar-primary': '#171717', 'sidebar-primary-foreground': '#FAFAFA',
    'sidebar-border': '#E5E5E5', 'sidebar-ring': '#A1A1A1', 'marca': '#171717', 'marca-foreground': '#FAFAFA',
    'sidebar-activo': '#D8D8D8', 'sidebar-activo-foreground': '#171717', 'sidebar-activo-borde': '#171717',
    'panel-ingreso': '#0A0A0A', 'panel-ingreso-foreground': '#FAFAFA', 'panel-ingreso-muted': '#A1A1A1',
  },
  oscuro: {
    'background': '#0A0A0A', 'foreground': '#FAFAFA', 'card': '#171717', 'card-foreground': '#FAFAFA',
    'popover': '#171717', 'popover-foreground': '#FAFAFA', 'muted': '#262626', 'secondary': '#262626', 'accent': '#262626',
    'secondary-foreground': '#FAFAFA', 'accent-foreground': '#FAFAFA', 'muted-foreground': '#A1A1A1',
    'border': '#FFFFFF1A', 'input': '#FFFFFF26', 'ring': '#737373', 'primary': '#E5E5E5', 'primary-foreground': '#171717',
    'sidebar': '#171717', 'sidebar-foreground': '#FAFAFA', 'sidebar-muted': '#B3B3B3', 'sidebar-accent': '#262626',
    'sidebar-accent-foreground': '#FAFAFA', 'sidebar-primary': '#FAFAFA', 'sidebar-primary-foreground': '#171717',
    'sidebar-border': '#FFFFFF1A', 'sidebar-ring': '#737373', 'marca': '#E5E5E5', 'marca-foreground': '#171717',
    'sidebar-activo': '#363636', 'sidebar-activo-foreground': '#E5E5E5', 'sidebar-activo-borde': '#E5E5E5',
    'panel-ingreso': '#0A0A0A', 'panel-ingreso-foreground': '#FAFAFA', 'panel-ingreso-muted': '#A1A1A1',
  },
  /* Juan piensa la base del Principal como negra, y lo es: la base sólo da el tono de los grises. */
  colores: { principal: '#171717', acento: '#171717', base: '#171717', barra: '#FAFAFA', fondo: '#FFFFFF', panel: '#0A0A0A' },
};

/** Los colores de un tema de la lista, para copiarlos a Personalizado (clonar). */
export function coloresDe(preset: Exclude<Preset, 'personalizado'>): Colores {
  return { ...(preset === 'principal' ? PRINCIPAL.colores : DE_LA_LISTA[preset]) };
}

/** Los juegos de tokens de un tema escogido. */
export function juegosDe(preset: Preset, colores: Colores): Juegos {
  if (preset === 'principal') return { claro: PRINCIPAL.claro, oscuro: PRINCIPAL.oscuro, notas: [] };
  return generarTema(colores.principal, colores.acento, colores.base, colores.barra, colores.fondo, colores.panel);
}

/** Lo que se le manda al núcleo en `PUT /api/merchant/:code` como `theme`. */
/** Los modelos del ingreso, de shadcn: login-02 · login-04 (teléfono: login-03) · login-01. */
export type ModeloIngreso = 'columnas' | 'dividida' | 'minimal';

export function temaParaGuardar(preset: Preset, colores: Colores, juegos: Juegos, logo?: string | null, login?: ModeloIngreso): any {
  // El logo y el modelo del ingreso no dependen del tema: van con el Principal también.
  const conLogo: any = logo ? { logo } : {};
  if (login && login !== 'columnas') conLogo.login = login;
  if (preset === 'principal') return { preset, ...conLogo };
  const recorte = (t: Tokens): Tokens => { const r: Tokens = {}; for (const k of TOKENS) r[k] = t[k]; return r; };
  return {
    ...conLogo,
    preset,
    colors: { primary: colores.principal, accent: colores.acento, base: colores.base, sidebar: colores.barra, background: colores.fondo, panel: colores.panel },
    light: recorte(juegos.claro),
    dark: recorte(juegos.oscuro),
  };
}

/** Un tema de la lista de temas guardados (núcleo: `portal_themes`). */
export interface TemaGuardado { _id: string; name: string; colores: Colores; }

/** Los colores de la pestaña, con los nombres del núcleo (para guardar en la lista). */
export function coloresAlNucleo(c: Colores): any {
  return { primary: c.principal, accent: c.acento, base: c.base, sidebar: c.barra, background: c.fondo, panel: c.panel };
}
/** Un tema guardado, como lo devuelve el núcleo, a los colores de la pestaña. */
export function temaDeLaLista(t: any): TemaGuardado {
  const c = t.colors || {};
  return { _id: t._id, name: t.name, colores: { principal: c.primary, acento: c.accent, base: c.base, barra: c.sidebar, fondo: c.background, panel: c.panel } };
}
export const mismosColores = (a: Colores, b: Colores): boolean =>
  (['principal', 'acento', 'base', 'barra', 'fondo', 'panel'] as const).every((k) => a[k] === b[k]);

/** De lo que devuelve el núcleo (`merchant.theme`) al estado de la pestaña. */
export function temaGuardado(theme: any): { preset: Preset; colores: Colores; logo: string | null; login: ModeloIngreso } {
  const preset: Preset = theme && ['creditotal', 'creditotal-2', 'personalizado'].includes(theme.preset) ? theme.preset : 'principal';
  const logo: string | null = (theme && typeof theme.logo === 'string') ? theme.logo : null;
  const login: ModeloIngreso = theme && ['dividida', 'minimal'].includes(theme.login) ? theme.login : 'columnas';
  if (preset === 'principal') return { preset, colores: { ...CREDITOTAL }, logo, login };
  const c = theme.colors || {};
  return {
    preset,
    logo,
    login,
    colores: {
      principal: c.primary || CREDITOTAL.principal,
      acento: c.accent || CREDITOTAL.acento,
      base: c.base || CREDITOTAL.base,
      // la primera versión guardaba «base» / «primary»; desde la segunda es un color
      barra: c.sidebar === 'primary' ? (c.primary || CREDITOTAL.principal)
        : (c.sidebar === 'base' || !c.sidebar) ? (c.base || CREDITOTAL.base) : c.sidebar,
      fondo: c.background || fondoConTinte(c.base || CREDITOTAL.base),
      panel: c.panel || '#0A0A0A',
    },
  };
}
